import keyBy from 'lodash/keyBy';

import { TFunction } from '@lyearn/i18n';
import {
  CheckMark,
  CheckmarkOutline,
  FillInTheBlanks,
  ImageFill,
  ListOutline,
  MatchFill,
  Menu3Outline,
  PinOutline,
  SmileyOutline,
  StarOutline,
  TrueFalseOutline,
} from '@lyearn/icons';
import { renderMediumOptionLabel } from '@/components/Form/components/Select/optionLabelRenderer';
import { renderMediumSelectValue } from '@/components/Form/components/Select/renderValue';
import { USER_SURVEY_QUESTION_TYPES } from '@/modules/user-survey/questions';
import { QuestionApplicableOnEnum, QuestionTypeEnum } from '@/types/schema';

import { formPicklistStyles } from '../modules/quiz/views/QuizEditor/components/QuestionEditor/components/QuestionsSettings/styles';

export interface QuestionTypePicklistOption {
  Icon: typeof CheckMark;
  value: QuestionTypeEnum;
  label: string;
  className?: string;
}

type Config = Partial<
  Record<QuestionTypeEnum, { Icon: typeof CheckMark; labelKey: string; value: QuestionTypeEnum }>
>;

export const QUESTION_TYPE_CONFIG: Config = {
  [QuestionTypeEnum.SingleSelect]: {
    Icon: CheckMark,
    labelKey: 'QuizPlayer:QuestionType.SingleSelect',
    value: QuestionTypeEnum.SingleSelect,
  },
  [QuestionTypeEnum.MultiSelect]: {
    Icon: CheckMark,
    labelKey: 'QuizPlayer:QuestionType.MultiSelect',
    value: QuestionTypeEnum.MultiSelect,
  },
  [QuestionTypeEnum.TrueOrFalse]: {
    Icon: TrueFalseOutline,
    labelKey: 'QuizPlayer:QuestionType.TrueOrFalse',
    value: QuestionTypeEnum.TrueOrFalse,
  },
  [QuestionTypeEnum.MatchTheFollowing]: {
    Icon: MatchFill,
    labelKey: 'QuizPlayer:QuestionType.MatchTheFollowing',
    value: QuestionTypeEnum.MatchTheFollowing,
  },
  [QuestionTypeEnum.FillInTheBlanks]: {
    Icon: FillInTheBlanks,
    labelKey: 'QuizPlayer:QuestionType.FillInTheBlanks',
    value: QuestionTypeEnum.FillInTheBlanks,
  },
  [QuestionTypeEnum.PictureMultipleChoiceQuestion]: {
    Icon: ImageFill,
    labelKey: 'QuizPlayer:QuestionType.PictureChoice',
    value: QuestionTypeEnum.PictureMultipleChoiceQuestion,
  },
  [QuestionTypeEnum.ImageHotspot]: {
    Icon: PinOutline,
    labelKey: 'QuizPlayer:QuestionType.ImageHotspot',
    value: QuestionTypeEnum.ImageHotspot,
  },
};

export const PERFORMANCE_REVIEW_QUESTION_TYPES = [
  {
    Icon: Menu3Outline,
    value: QuestionTypeEnum.Text,
    labelKey: 'UserSurvey:QuestionTypes.Text',
  },
  {
    Icon: CheckmarkOutline,
    value: QuestionTypeEnum.SingleSelect,
    labelKey: 'UserSurvey:QuestionTypes.Select',
  },
  {
    Icon: ListOutline,
    value: QuestionTypeEnum.MultiSelect,
    labelKey: 'UserSurvey:QuestionTypes.MultiSelect',
  },
  {
    Icon: SmileyOutline,
    value: QuestionTypeEnum.Likert,
    labelKey: 'UserSurvey:QuestionTypes.Likert',
  },
  {
    Icon: StarOutline,
    value: QuestionTypeEnum.Rating,
    labelKey: 'UserSurvey:QuestionTypes.Rating',
  },
];

export const getQuestionConfig = (applicableOn: QuestionApplicableOnEnum) => {
  switch (applicableOn) {
    case QuestionApplicableOnEnum.UserSurvey: {
      return keyBy(USER_SURVEY_QUESTION_TYPES, 'value');
    }
    case QuestionApplicableOnEnum.PerformanceReview: {
      return keyBy(PERFORMANCE_REVIEW_QUESTION_TYPES, 'value');
    }
    default: {
      return QUESTION_TYPE_CONFIG;
    }
  }
};

export const getQuestionTypePicklistOptions = (
  t: TFunction,
  applicableOn: QuestionApplicableOnEnum,
): QuestionTypePicklistOption[] => {
  const config = getQuestionConfig(applicableOn);

  return Object.values(config).map(({ Icon, labelKey, value }) => ({
    Icon,
    value,
    label: t(labelKey),
  }));
};

export const FORM_PICKLIST_PROPS = {
  className: formPicklistStyles,
  optionLabelRenderer: renderMediumOptionLabel,
  renderValue: renderMediumSelectValue,
};
